/**
 * Undo Toast Manager
 * Lightweight observable store for triggering and managing interactive Undo toasts
 * across desktop and mobile views.
 */

export interface UndoToastData {
  id: string;
  message: string;
  actionLabel?: string;
  onUndo?: () => void | Promise<void>;
  icon?: 'delete' | 'move' | 'status' | 'info';
  durationMs?: number;
  /** Additional inline actions (e.g. 'Folgevorgang'), rendered before the undo button. */
  actions?: UndoToastAction[];
  timestamp: number;
}

export interface UndoToastAction {
  label: string;
  onClick: () => void | Promise<void>;
}

export type UndoToastListener = (toast: UndoToastData | null) => void;

class UndoToastManager {
  private currentToast: UndoToastData | null = null;
  private listeners = new Set<UndoToastListener>();
  private dismissTimer: ReturnType<typeof setTimeout> | null = null;

  public show(options: {
    message: string;
    actionLabel?: string;
    onUndo?: () => void | Promise<void>;
    icon?: 'delete' | 'move' | 'status' | 'info';
    durationMs?: number;
    actions?: UndoToastAction[];
  }): void {
    if (this.dismissTimer) {
      clearTimeout(this.dismissTimer);
      this.dismissTimer = null;
    }

    const duration = options.durationMs || 6000;
    const toast: UndoToastData = {
      id: `toast-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
      message: options.message,
      actionLabel: options.actionLabel || 'Rückgängig',
      onUndo: options.onUndo,
      icon: options.icon || 'info',
      durationMs: duration,
      actions: options.actions,
      timestamp: Date.now()
    };

    this.currentToast = toast;
    this.notify();

    this.dismissTimer = setTimeout(() => {
      this.dismiss();
    }, duration);
  }

  public dismiss(): void {
    if (this.dismissTimer) {
      clearTimeout(this.dismissTimer);
      this.dismissTimer = null;
    }
    this.currentToast = null;
    this.notify();
  }

  public getToast(): UndoToastData | null {
    return this.currentToast;
  }

  public subscribe(listener: UndoToastListener): () => void {
    this.listeners.add(listener);
    listener(this.currentToast);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notify(): void {
    this.listeners.forEach(fn => {
      try {
        fn(this.currentToast);
      } catch (err) {
        console.error('[UndoToastManager] Listener error:', err);
      }
    });
  }
}

export const undoToastManager = new UndoToastManager();
